import { useState, useCallback } from 'react';
import {
  skinUndertoneService,
  UndertoneType,
  ColorPalette,
  MakeupRecommendations,
  SeasonPrediction,
} from '../services/SkinUndertoneService';
import type { SkinAnalysisRegion } from '../utils/faceRoi';

interface UseSkinUndertoneReturn {
  undertone: UndertoneType | null;
  colorPalette: ColorPalette | null;
  seasonPrediction: SeasonPrediction | null;
  makeupRecommendations: MakeupRecommendations | null;
  isLoading: boolean;
  error: string | null;
  analyzeUndertone: (imageData: ImageData) => Promise<void>;
  analyzeUndertoneWithTensorFlow: (imageData: ImageData) => Promise<void>;
  analyzeUndertoneAdvanced: (
    imageData: ImageData,
    faceRegion?: SkinAnalysisRegion | null
  ) => Promise<UndertoneType | null>;
  getColorPalette: (undertone: UndertoneType) => void;
  getAllColorPalettes: (undertone: UndertoneType) => ColorPalette[];
  getMakeupRecommendations: (undertone: UndertoneType) => void;
  setUndertone: (undertone: UndertoneType) => void;
  reset: () => void;
}

export const useSkinUndertone = (): UseSkinUndertoneReturn => {
  const [undertone, setUndertone] = useState<UndertoneType | null>(null);
  const [colorPalette, setColorPalette] = useState<ColorPalette | null>(null);
  const [seasonPrediction, setSeasonPrediction] =
    useState<SeasonPrediction | null>(null);
  const [makeupRecommendations, setMakeupRecommendations] =
    useState<MakeupRecommendations | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Resolve palette + seasonal prediction from an undertone result. */
  const resolveSeasonalPalette = useCallback(async (result: UndertoneType) => {
    const skinData = skinUndertoneService.getSkinToneData();
    const dominantColors =
      skinData?.dominantColors.map((c) => ({
        hex: c.hex,
      })) ?? [];

    const { palette, prediction } =
      await skinUndertoneService.getSeasonalPalette(result, dominantColors);

    const makeup = skinUndertoneService.getMakeupRecommendations(result);
    setColorPalette(palette);
    setSeasonPrediction(prediction);
    setMakeupRecommendations(makeup);
  }, []);

  const analyzeUndertone = useCallback(
    async (imageData: ImageData) => {
      setIsLoading(true);
      setError(null);
      try {
        const result =
          await skinUndertoneService.analyzeSkinUndertone(imageData);
        setUndertone(result);
        await resolveSeasonalPalette(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to analyze skin undertone'
        );
        console.error('Error in useSkinUndertone:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [resolveSeasonalPalette]
  );

  const analyzeUndertoneWithTensorFlow = useCallback(
    async (imageData: ImageData) => {
      setIsLoading(true);
      setError(null);
      try {
        const result =
          await skinUndertoneService.analyzeSkinUndertoneWithTensorFlow(
            imageData
          );
        setUndertone(result);
        await resolveSeasonalPalette(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to analyze skin undertone with TensorFlow'
        );
        console.error('Error in useSkinUndertone TensorFlow:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [resolveSeasonalPalette]
  );

  const analyzeUndertoneAdvanced = useCallback(
    async (imageData: ImageData, faceRegion?: SkinAnalysisRegion | null) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await skinUndertoneService.analyzeSkinUndertoneAdvanced(
          imageData,
          faceRegion ?? undefined
        );
        setUndertone(result);
        await resolveSeasonalPalette(result);
        return result;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to analyze skin undertone advanced'
        );
        console.error('Error in useSkinUndertone advanced:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [resolveSeasonalPalette]
  );

  const getColorPalette = useCallback((undertoneType: UndertoneType) => {
    try {
      const palette = skinUndertoneService.getColorPalette(undertoneType);
      setColorPalette(palette);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to get color palette'
      );
    }
  }, []);

  const getAllColorPalettes = useCallback((undertoneType: UndertoneType) => {
    try {
      return skinUndertoneService.getAllColorPalettes(undertoneType);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to get all color palettes'
      );
      return [];
    }
  }, []);

  const getMakeupRecommendations = useCallback(
    (undertoneType: UndertoneType) => {
      try {
        const makeup =
          skinUndertoneService.getMakeupRecommendations(undertoneType);
        setMakeupRecommendations(makeup);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to get makeup recommendations'
        );
      }
    },
    []
  );

  const reset = useCallback(() => {
    setUndertone(null);
    setColorPalette(null);
    setSeasonPrediction(null);
    setMakeupRecommendations(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    undertone,
    colorPalette,
    seasonPrediction,
    makeupRecommendations,
    isLoading,
    error,
    analyzeUndertone,
    analyzeUndertoneWithTensorFlow,
    analyzeUndertoneAdvanced,
    getColorPalette,
    getAllColorPalettes,
    getMakeupRecommendations,
    setUndertone,
    reset,
  };
};
